// src/pages/Licencas.jsx
import React, { useEffect, useState, useRef } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, TextField, MenuItem, Grid, IconButton,
  TableSortLabel, Select, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, Link
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Link as RouterLink } from 'react-router-dom';
import api from '../services/api';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockIcon from '@mui/icons-material/Lock';
import EditIcon from '@mui/icons-material/Edit';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import FileDownload from '@mui/icons-material/FileDownload';
import ClearIcon from '@mui/icons-material/Clear';
import confetti from 'canvas-confetti';
import { saveAs } from 'file-saver';

// Modal já existente no seu projeto
import AtivacoesModal from '../components/AtivacoesModal';
// 🔎 novo componente compartilhado
import SearchUserAutocomplete from '../components/SearchUserAutocomplete';

// rota da licença
const toLicenca = (chave) => `/licencas/novo/${encodeURIComponent(chave)}`;

// monta URL do WhatsApp a partir do telefone
const toWhatsUrl = (telefone) => {
  if (!telefone) return null;
  let digits = String(telefone).replace(/\D/g, '');
  if (!digits) return null;

  if (digits.startsWith('0')) digits = digits.slice(1);

  if (!digits.startsWith('55') && digits.length >= 10) {
    digits = '55' + digits;
  }

  return `https://wa.me/${digits}`;
};

// trata "2025-12-03" ou "2025-12-03T..." sem quebrar o dia
const parseLocalYMD = (valor) => {
  if (!valor) return null;
  const s = String(valor);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) {
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  }
  const ano = Number(m[1]);
  const mes = Number(m[2]);
  const dia = Number(m[3]);
  return new Date(ano, mes - 1, dia, 0, 0, 0);
};

const formataData = (dataStr) => {
  if (!dataStr) return '-';
  const s = String(dataStr);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const [, ano, mes, dia] = m;
    return `${dia}/${mes}/${ano}`;
  }
  const d = parseLocalYMD(s);
  if (!d) return s;
  const dia = String(d.getDate()).padStart(2, '0');
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const ano = d.getFullYear();
  return `${dia}/${mes}/${ano}`;
};

const hojeLocal = () => {
  const hoje = new Date();
  return new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
};

const calculaDiasRestantes = (validade) => {
  if (!validade) return '-';
  const dataValidade = parseLocalYMD(validade);
  if (!dataValidade) return '-';

  const diffTime = dataValidade - hojeLocal();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays >= 0 ? diffDays : 0;
};

function Licencas() {
  const navigate = useNavigate();

  const [licencas, setLicencas] = useState([]);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Filtros
  const [busca, setBusca] = useState('');
  const [campoData, setCampoData] = useState('validade');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [status, setStatus] = useState('');
  const [webhook, setWebhook] = useState('');

  // Ordenação
  const [sortField, setSortField] = useState('l.id');
  const [sortOrder, setSortOrder] = useState('desc');

  // Modal Ativações
  const [modalAtivacoes, setModalAtivacoes] = useState({ open: false, licenca: null });

  // --- Modal Trocar Revendedor (apenas admin) ---
  const [revModal, setRevModal] = useState({ open: false, licenca: null });
  const [revSelecionado, setRevSelecionado] = useState(null);
  const [salvandoRev, setSalvandoRev] = useState(false);

  // Copiar chave
  const [copiedChave, setCopiedChave] = useState(null);
  const copiarChave = (chave) => {
    navigator.clipboard.writeText(chave);
    setCopiedChave(chave);
    setTimeout(() => setCopiedChave(null), 1200);
  };

  // usuário logado
  const userTipo = localStorage.getItem('tipo');
  const isAdmin = userTipo === 'admin';
  const minhaChave = localStorage.getItem('chave_usuario') || '';

  // --------- NOVO: filtro de revendedor (só admin) ---------
  const [revFiltroSel, setRevFiltroSel] = useState(null); // { nome, chave } | null

  // Debounce para busca
  const buscaTimeout = useRef();

  // Colunas
  const columns = [
    ...(isAdmin ? [{ label: 'Usuário', field: 'usuario_nome' }] : []),
    { label: 'Chave', field: 'l.chave' },
    { label: 'Plano', field: 'l.tipo_plano' },
    { label: 'Cliente', field: 'l.cliente_nome' },
    { label: 'Telefone', field: 'l.telefone' },
    { label: 'Cadastro', field: 'l.criado_em' },
    { label: 'Renovação', field: 'l.data_renovacao' },
    { label: 'Vencimento', field: 'l.validade' },
    { label: 'Dias Restantes', field: '' },
    { label: 'Ativações', field: '' },
    { label: 'Versão', field: 'l.versao_programa' },
    { label: 'GPT', field: '' },
    { label: 'Flow', field: '' },
    { label: 'Kit', field: '' },
    { label: 'Valor', field: 'l.valor' },
    { label: 'Status', field: 'l.status' },
    { label: 'Ações', field: '' }
  ];

  // ========= montagem de parâmetros (com regra admin x não-admin) =========
  const montarParams = (overrides = {}) => {
    const p = {
      page,
      pageSize,
      busca,
      campoData,
      dataInicio,
      dataFim,
      status,
      webhook,
      sortField,
      sortOrder,
      userTipo,
      ...overrides,
    };

    // Não-admin: sempre sua chave. Admin: só filtra se escolheu alguém.
    const filtroChave = isAdmin ? (revFiltroSel?.chave || '') : minhaChave;
    if (filtroChave) p.chave_usuario = filtroChave;

    return p;
  };

  // Buscar licenças
  const carregarLicencas = async (overrides) => {
    try {
      const params = montarParams(overrides);
      const { data } = await api.get('/licencas', { params });
      setLicencas(data.registros);
      setTotalRegistros(data.total);
    } catch (err) {
      console.error('Erro ao carregar licenças:', err);
      alert('Erro ao carregar licenças.');
    }
  };

  // Busca automática (campo "Chave ou Nome")
  useEffect(() => {
    if (buscaTimeout.current) clearTimeout(buscaTimeout.current);
    buscaTimeout.current = setTimeout(() => {
      setPage(1);
      carregarLicencas({ page: 1 });
    }, 350);
    // eslint-disable-next-line
  }, [busca]);

  // Demais dependências
  useEffect(() => {
    carregarLicencas();
    // eslint-disable-next-line
  }, [page, pageSize, sortField, sortOrder, revFiltroSel?.chave]);

  const handleSort = (field) => {
    const newOrder = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder(newOrder);
  };

  const handleFiltrar = () => {
    setPage(1);
    carregarLicencas({ page: 1 });
  };

  const handleLimpar = () => {
    setBusca('');
    setCampoData('validade');
    setDataInicio('');
    setDataFim('');
    setStatus('');
    setWebhook('');
    setRevFiltroSel(null);
    setPage(1);
    setSortField('l.id');
    setSortOrder('desc');
    carregarLicencas({ page: 1, sortField: 'l.id', sortOrder: 'desc' });
  };

  const handleBloquear = async (licenca) => {
    try {
      const novoStatus = licenca.status === 'ativo' ? 'inativo' : 'ativo';
      await api.put(`/licencas/${licenca.chave}/status`, { status: novoStatus });
      carregarLicencas();

      if (novoStatus === 'ativo') {
        confetti({ particleCount: 180, spread: 80, origin: { y: 0.6 } });
      }
    } catch (err) {
      console.error('Erro ao bloquear/desbloquear licença:', err);
      alert('Erro ao bloquear/desbloquear licença.');
    }
  };

  const handleEditar = (chave) => {
    navigate(toLicenca(chave));
  };

  const totalPaginas = Math.ceil(totalRegistros / pageSize);

  const BoolDot = ({ ativo, vencido }) => (
    <Tooltip title={ativo ? (vencido ? 'Permissão vencida' : 'Permissão ativa') : 'Sem permissão'}>
      <FiberManualRecordIcon
        sx={{
          fontSize: 18,
          color: !ativo ? '#bdbdbd' : vencido ? '#ffa726' : '#43a047',
          verticalAlign: 'middle'
        }}
      />
    </Tooltip>
  );

  const exportarCSV = () => {
    const sep = ';';
    const toCSV = (v) => {
      if (v === null || v === undefined) return '""';
      const s = String(v).replace(/"/g, '""');
      return `"${s}"`;
    };
    const isAtivo = (x) => x === 1 || x === true || x === '1' || x === 'true';

    const header = [
      ...(isAdmin ? ['Usuário', 'Chave Revendedor'] : []),
      'Chave','Plano','Cliente','Telefone',
      'Cadastro','Renovação','Vencimento','Dias Restantes',
      'Ativações','Versão','GPT','Flow','Kit','Valor','Status'
    ];

    const linhas = licencas.map(l => {
      const dias = calculaDiasRestantes(l.validade);
      const ativacoes = `${l.qtd_ativacoes} de ${l.max_ativacoes}`;
      const valor = (l.valor !== undefined && l.valor !== null)
        ? Number(l.valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
        : '-';

      const gpt = isAtivo(l.pode_usar_gpt) ? 'Ativo' : 'Inativo';
      const flow = isAtivo(l.pode_usar_flow) ? 'Ativo' : 'Inativo';
      const kit = isAtivo(l.kit_blindagem) ? 'Ativo' : 'Inativo';

      const planoStr = l.tipo_plano || '-';
      const origemStr = l.origem_venda ? ` (${l.origem_venda})` : '';

      const base = [
        l.chave,
        planoStr + origemStr,
        l.cliente_nome || '-',
        l.telefone || '-',
        formataData(l.criado_em),
        formataData(l.data_renovacao),
        formataData(l.validade),
        dias,
        ativacoes,
        l.versao_programa || '-',
        gpt, flow, kit,
        valor,
        l.status || '-'
      ];

      return (isAdmin
        ? [l.usuario_nome || '-', l.chave_usuario || '-', ...base]
        : base
      ).map(toCSV).join(sep);
    });

    const conteudo = [header.join(sep), ...linhas].join('\r\n');
    const blob = new Blob(["\uFEFF" + conteudo], { type: 'text/csv;charset=utf-8;' });

    const hoje = new Date();
    const y = hoje.getFullYear();
    const m = String(hoje.getMonth() + 1).padStart(2, '0');
    const d = String(hoje.getDate()).padStart(2, '0');
    saveAs(blob, `licencas_${y}${m}${d}.csv`);
  };

  const abrirAtivacoes = (licenca) => setModalAtivacoes({ open: true, licenca });
  const fecharAtivacoes = () => setModalAtivacoes({ open: false, licenca: null });

  const abrirRevendedor = (licenca) => {
    setRevModal({ open: true, licenca });
    setRevSelecionado({
      nome: licenca.usuario_nome || '',
      chave: licenca.chave_usuario || ''
    });
  };

  const fecharRevendedor = () => {
    if (salvandoRev) return;
    setRevModal({ open: false, licenca: null });
    setRevSelecionado(null);
  };

  const salvarRevendedor = async () => {
    if (!revSelecionado?.chave) {
      alert('Selecione um revendedor válido.');
      return;
    }
    try {
      setSalvandoRev(true);
      await api.put(`/licencas/${revModal.licenca.chave}/revendedor`, {
        chave_usuario: revSelecionado.chave
      });
      fecharRevendedor();
      carregarLicencas();
    } catch (e) {
      console.error('Erro ao salvar revendedor:', e);
      alert('Não foi possível atualizar o revendedor.');
    } finally {
      setSalvandoRev(false);
    }
  };

  return (
    <Box p={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Licenças</Typography>
        <Button
          variant="contained"
          startIcon={<AddCircleOutlineIcon />}
          sx={{
            backgroundColor: '#ffeb3b',
            color: '#000',
            borderRadius: '999px',
            padding: '6px 16px',
            fontWeight: 'bold',
            boxShadow: '0px 2px 5px rgba(0,0,0,0.12)',
            '&:hover': { backgroundColor: '#fdd835' }
          }}
          onClick={() => navigate('/licencas/novo')}
        >
          NOVA LICENÇA
        </Button>
      </Box>

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          {/* filtro de revendedor (só admin) */}
          {isAdmin && (
            <>
              <Grid item xs={12} sm={6} md={4}>
                <SearchUserAutocomplete
                  value={revFiltroSel}
                  onChange={(val) => {
                    setRevFiltroSel(val);
                    setPage(1);
                    carregarLicencas({ page: 1 });
                  }}
                />
              </Grid>

              <Grid item xs={12} sm="auto">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={() => {
                    setRevFiltroSel(null);
                    setPage(1);
                    carregarLicencas({ page: 1 });
                  }}
                >
                  Ver todos
                </Button>
              </Grid>

              <Grid item xs={12} />
            </>
          )}

          <Grid item xs={12} sm={3}>
            <TextField
              label="Chave, Nome ou Telefone"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              fullWidth
              size="small"
            />
          </Grid>

          <Grid item xs={12} sm={2}>
            <TextField
              select
              label="Qual data?"
              value={campoData}
              onChange={(e) => setCampoData(e.target.value)}
              fullWidth
              size="small"
            >
              <MenuItem value="criado_em">Cadastro</MenuItem>
              <MenuItem value="data_renovacao">Renovação</MenuItem>
              <MenuItem value="validade">Vencimento</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} sm={2}>
            <TextField
              label="Data Inicial"
              type="date"
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Grid>

          <Grid item xs={12} sm={2}>
            <TextField
              label="Data Final"
              type="date"
              value={dataFim}
              onChange={(e) => setDataFim(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Grid>

          {/* ⚠️ FIX: Grid não aceita sm={1.5}. Use inteiros. */}
          <Grid item xs={12} sm={1}>
            <TextField
              select
              label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              fullWidth
              size="small"
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="ativo">Ativo</MenuItem>
              <MenuItem value="inativo">Inativo</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} sm={2}>
            <TextField
              select
              label="Webhook"
              value={webhook}
              onChange={(e) => setWebhook(e.target.value)}
              fullWidth
              size="small"
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="1">Somente Webhook</MenuItem>
              <MenuItem value="0">Sem Webhook</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} sm="auto">
            <Box display="flex" gap={1}>
              <Button variant="contained" color="primary" onClick={handleFiltrar}>
                FILTRAR
              </Button>
              <Button variant="outlined" color="secondary" onClick={handleLimpar}>
                LIMPAR
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabela */}
      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table
          sx={{
            minWidth: 1200,
            '& th, & td': { fontSize: '13px', padding: '4px 4px', whiteSpace: 'nowrap' },
            '& thead th': { backgroundColor: '#1976d2', color: 'white', fontWeight: 'bold' },
            '& tbody tr:nth-of-type(odd)': { backgroundColor: '#f9f9f9' },
            '& tbody tr:nth-of-type(even)': { backgroundColor: '#fff' }
          }}
        >
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col.label} align="center">
                  {col.field ? (
                    <TableSortLabel
                      active={sortField === col.field}
                      direction={sortField === col.field ? sortOrder : 'asc'}
                      onClick={() => handleSort(col.field)}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : (
                    col.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {licencas.map((licenca) => (
              <TableRow key={licenca.chave}>
                {/* USUÁRIO (nome + chave do revendedor) */}
                {isAdmin && (
                  <TableCell align="left">
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span>{licenca.usuario_nome || '-'}</span>
                      <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#616161' }}>
                        {licenca.chave_usuario}
                      </span>
                    </div>
                  </TableCell>
                )}

                {/* CHAVE DA LICENÇA (link + copiar) */}
                <TableCell
                  align="center"
                  sx={{ whiteSpace: 'nowrap', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  <Tooltip title={licenca.chave}>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', maxWidth: 220 }}>
                      <Link
                        component={RouterLink}
                        to={toLicenca(licenca.chave)}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: 13,
                          maxWidth: 170,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {licenca.chave}
                      </Link>

                      <Tooltip title={copiedChave === licenca.chave ? 'Copiado!' : 'Copiar'}>
                        <IconButton
                          onClick={() => copiarChave(licenca.chave)}
                          size="small"
                          sx={{ ml: 0.5 }}
                        >
                          <ContentCopyIcon
                            fontSize="inherit"
                            color={copiedChave === licenca.chave ? 'success' : 'action'}
                          />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Tooltip>
                </TableCell>

                {/* PLANO */}
                <TableCell align="center" sx={{ textTransform: 'capitalize' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
                    <span>{licenca.tipo_plano || '-'}</span>

                    {licenca.origem_venda && (
                      <span style={{ fontSize: 11, color: '#616161', textTransform: 'uppercase' }}>
                        {licenca.origem_venda}
                      </span>
                    )}

                    {licenca.codigo_venda && (
                      <span style={{ fontSize: 10, color: '#9e9e9e', fontFamily: 'monospace' }}>
                        {licenca.codigo_venda}
                      </span>
                    )}
                  </div>
                </TableCell>

                {/* CLIENTE */}
                <TableCell
                  align="left"
                  sx={{ maxWidth: 120, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  <Tooltip title={licenca.cliente_nome}>
                    <span>{licenca.cliente_nome || '-'}</span>
                  </Tooltip>
                </TableCell>


                <TableCell align="center">
                  <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15 }}>
                    {(() => {
                      const url = toWhatsUrl(licenca.telefone);
                      if (!url) return <span style={{ fontFamily: 'monospace' }}>{licenca.telefone || '-'}</span>;
                      return (
                        <Link
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          sx={{ fontFamily: 'monospace', fontSize: 13 }}
                        >
                          {licenca.telefone}
                        </Link>
                      );
                    })()}

                    {isAdmin && licenca.telefone_webhook && (
                      (() => {
                        const url2 = toWhatsUrl(licenca.telefone_webhook);
                        if (!url2) {
                          return (
                            <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#616161' }}>
                              {licenca.telefone_webhook}
                            </span>
                          );
                        }
                        return (
                          <Link
                            href={url2}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            sx={{ fontFamily: 'monospace', fontSize: 12, color: '#616161' }}
                          >
                            {licenca.telefone_webhook}
                          </Link>
                        );
                      })()
                    )}
                  </div>
                </TableCell>

                <TableCell align="center">{formataData(licenca.criado_em)}</TableCell>
                <TableCell align="center">{formataData(licenca.data_renovacao)}</TableCell>
                <TableCell align="center">{formataData(licenca.validade)}</TableCell>
                <TableCell align="center">{calculaDiasRestantes(licenca.validade)}</TableCell>

                <TableCell align="center">
                  <Button size="small" variant="text" onClick={() => abrirAtivacoes(licenca)}>
                    {licenca.qtd_ativacoes} de {licenca.max_ativacoes}
                  </Button>
                </TableCell>

                <TableCell align="center">{licenca.versao_programa || '-'}</TableCell>

                <TableCell align="center">
                  <BoolDot
                    ativo={licenca.pode_usar_gpt === 1 || licenca.pode_usar_gpt === true}
                    vencido={
                      (licenca.pode_usar_gpt === 1 || licenca.pode_usar_gpt === true) &&
                      (() => {
                        const v = parseLocalYMD(licenca.pode_usar_gpt_vencimento);
                        if (!v) return false;
                        return v < hojeLocal();
                      })()
                    }
                  />
                </TableCell>

                <TableCell align="center">
                  <BoolDot
                    ativo={licenca.pode_usar_flow === 1 || licenca.pode_usar_flow === true}
                    vencido={
                      (licenca.pode_usar_flow === 1 || licenca.pode_usar_flow === true) &&
                      (() => {
                        const v = parseLocalYMD(licenca.pode_usar_flow_vencimento);
                        if (!v) return false;
                        return v < hojeLocal();
                      })()
                    }
                  />
                </TableCell>

                <TableCell align="center">
                  <BoolDot
                    ativo={licenca.kit_blindagem === 1 || licenca.kit_blindagem === true}
                    vencido={
                      (licenca.kit_blindagem === 1 || licenca.kit_blindagem === true) &&
                      (() => {
                        const v = parseLocalYMD(licenca.kit_blindagem_vencimento);
                        if (!v) return false;
                        return v < hojeLocal();
                      })()
                    }
                  />
                </TableCell>

                <TableCell align="center">
                  {licenca.valor !== undefined && licenca.valor !== null
                    ? Number(licenca.valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                    : '-'}
                </TableCell>

                <TableCell align="center">
                  <Tooltip title={licenca.status === 'ativo' ? 'Ativo' : 'Inativo'}>
                    <span style={{ fontSize: 24 }}>{licenca.status === 'ativo' ? '🟢' : '🔴'}</span>
                  </Tooltip>
                </TableCell>

                <TableCell align="center">
                  <Box display="flex" justifyContent="center" gap={1}>
                    {/* Trocar revendedor — só admin */}
                    {isAdmin && (
                      <Tooltip title="Trocar revendedor">
                        <span>
                          <IconButton size="small" color="secondary" onClick={() => abrirRevendedor(licenca)}>
                            <VpnKeyIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}

                    <IconButton onClick={() => handleBloquear(licenca)} color="warning" size="small">
                      {licenca.status === 'ativo' ? <LockOpenIcon /> : <LockIcon />}
                    </IconButton>

                    <IconButton onClick={() => handleEditar(licenca.chave)} color="primary" size="small">
                      <EditIcon />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Rodapé */}
      <Box mt={2} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        {/* Esquerda: Mostrar */}
        <Box display="flex" alignItems="center" gap={1}>
          <Typography>Mostrar:</Typography>
          <Select
            value={pageSize}
            onChange={(e) => {
              setPageSize(e.target.value);
              setPage(1);
            }}
            size="small"
          >
            {[10, 25, 50, 100, 250, 500].map((size) => (
              <MenuItem key={size} value={size}>
                {size}
              </MenuItem>
            ))}
          </Select>
        </Box>

        {/* Centro: Paginação */}
        <Box display="flex" alignItems="center" gap={2} justifyContent="center" flex="1">
          <Button
            variant="outlined"
            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
            disabled={page === 1}
          >
            Anterior
          </Button>
          <Typography>
            Página {page} de {totalPaginas}
          </Typography>
          <Button
            variant="outlined"
            onClick={() => setPage((prev) => Math.min(prev + 1, totalPaginas))}
            disabled={page === totalPaginas || totalPaginas === 0}
          >
            Próxima
          </Button>
        </Box>

        {/* Direita: Exportar */}
        <Box display="flex" alignItems="center" gap={1}>
          <Button
            variant="outlined"
            startIcon={<FileDownload />}
            onClick={exportarCSV}
            sx={{ borderRadius: '999px', fontWeight: 'bold' }}
          >
            EXPORTAR CSV
          </Button>
        </Box>
      </Box>

      {/* Modal de ativações */}
      <AtivacoesModal
        open={modalAtivacoes.open}
        onClose={fecharAtivacoes}
        licenca={modalAtivacoes.licenca}
        isAdmin={isAdmin}
      />

      {/* Modal Trocar Revendedor (ADMIN) */}
      <Dialog open={revModal.open} onClose={fecharRevendedor} maxWidth="sm" fullWidth>
        <DialogTitle>Trocar revendedor da licença {revModal.licenca?.chave}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Atual: <b>{revModal.licenca?.usuario_nome || '-'}</b>{' '}
            <span style={{ fontFamily: 'monospace', color: '#616161' }}>
              {revModal.licenca?.chave_usuario ? `(${revModal.licenca.chave_usuario})` : ''}
            </span>
          </Typography>

          <SearchUserAutocomplete
            value={revSelecionado}
            onChange={(v) => setRevSelecionado(v)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={fecharRevendedor} disabled={salvandoRev}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={salvarRevendedor}
            disabled={salvandoRev || !revSelecionado?.chave}
          >
            {salvandoRev ? 'Salvando…' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Licencas;
